import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Receipt, Plus, Loader2, Trash2, Upload, Car, CheckCircle2, XCircle, Send } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const STATUS_MAP = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  soumise: { label: 'Soumise', color: 'bg-blue-100 text-blue-700' },
  validee: { label: 'Validée', color: 'bg-green-100 text-green-700' },
  refusee: { label: 'Refusée', color: 'bg-red-100 text-red-700' },
  remboursee: { label: 'Remboursée', color: 'bg-emerald-100 text-emerald-700' },
};

export default function NotesDeFrais() {
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titre: '', mois: new Date().toISOString().slice(0, 7), lignes: [{ date: new Date().toISOString().slice(0, 10), categorie: 'restaurant', description: '', montant_ttc: 0, taux_tva: 20, is_kilometrique: false }], notes: '' });

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/notes-frais`, { params: tab !== 'all' ? { statut: tab } : {} }),
      axios.get(`${API_URL}/notes-frais/categories`),
    ]).then(([n, c]) => { setNotes(n.data); setCategories(c.data); }).catch(() => {}).finally(() => setLoading(false));
  }, [tab]);

  const addLine = () => setForm({ ...form, lignes: [...form.lignes, { date: new Date().toISOString().slice(0, 10), categorie: 'restaurant', description: '', montant_ttc: 0, taux_tva: 20, is_kilometrique: false }] });
  const removeLine = (i) => setForm({ ...form, lignes: form.lignes.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, value) => { const l = [...form.lignes]; l[i] = { ...l[i], [field]: value }; setForm({ ...form, lignes: l }); };
  const total = form.lignes.reduce((s, l) => s + (l.is_kilometrique ? (l.km_montant_calcule || 0) : (l.montant_ttc || 0)), 0);

  const handleCreate = async () => {
    if (!form.titre) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/notes-frais`, form);
      toast.success('Note de frais créée');
      setCreateOpen(false);
      const res = await axios.get(`${API_URL}/notes-frais`);
      setNotes(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.post(`${API_URL}/notes-frais/${id}/${action}`);
      toast.success(action === 'soumettre' ? 'Note soumise' : action === 'valider' ? 'Note validée' : action === 'rembourser' ? 'Note remboursée' : 'Action effectuée');
      const res = await axios.get(`${API_URL}/notes-frais`, { params: tab !== 'all' ? { statut: tab } : {} });
      setNotes(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold"><Receipt className="w-6 h-6 inline mr-2" />Notes de frais</h1><p className="text-slate-500">{notes.length} note(s)</p></div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" /> Nouvelle note</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList><TabsTrigger value="all">Toutes</TabsTrigger><TabsTrigger value="brouillon">Brouillons</TabsTrigger><TabsTrigger value="soumise">Soumises</TabsTrigger><TabsTrigger value="validee">Validées</TabsTrigger><TabsTrigger value="remboursee">Remboursées</TabsTrigger></TabsList>
      </Tabs>

      <Card><CardContent className="p-0">
        <Table><TableHeader><TableRow><TableHead>Titre</TableHead><TableHead>Mois</TableHead><TableHead>Collaborateur</TableHead><TableHead>Lignes</TableHead><TableHead>Montant</TableHead><TableHead>Statut</TableHead><TableHead className="w-40">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {notes.map(n => (
            <TableRow key={n.id}>
              <TableCell className="font-medium">{n.titre}</TableCell>
              <TableCell>{n.mois}</TableCell>
              <TableCell className="text-sm">{n.user_name || '-'}</TableCell>
              <TableCell>{n.lignes?.length || 0}</TableCell>
              <TableCell className="font-mono font-medium">{formatCurrency(n.montant_total)}</TableCell>
              <TableCell><Badge className={`${STATUS_MAP[n.statut]?.color || ''} text-xs`}>{STATUS_MAP[n.statut]?.label || n.statut}</Badge></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {n.statut === 'brouillon' && <Button variant="ghost" size="sm" onClick={() => handleAction(n.id, 'soumettre')}><Send className="w-3 h-3 mr-1" />Soumettre</Button>}
                  {n.statut === 'soumise' && <Button variant="ghost" size="sm" onClick={() => handleAction(n.id, 'valider')}><CheckCircle2 className="w-3 h-3 mr-1" />Valider</Button>}
                  {n.statut === 'validee' && <Button variant="ghost" size="sm" onClick={() => handleAction(n.id, 'rembourser')}>Rembourser</Button>}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {notes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">Aucune note de frais</TableCell></TableRow>}
        </TableBody></Table>
      </CardContent></Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle note de frais</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Titre *</Label><Input value={form.titre} onChange={e => setForm({...form, titre: e.target.value})} placeholder="Déplacement client Mars 2026" /></div>
              <div><Label>Mois</Label><Input type="month" value={form.mois} onChange={e => setForm({...form, mois: e.target.value})} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-base font-semibold">Dépenses</Label><Button variant="outline" size="sm" onClick={addLine}><Plus className="w-3 h-3 mr-1" /> Ligne</Button></div>
              <Table><TableHeader><TableRow><TableHead className="w-28">Date</TableHead><TableHead className="w-32">Catégorie</TableHead><TableHead>Description</TableHead><TableHead className="w-20">KM?</TableHead><TableHead className="w-24">Montant</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
              <TableBody>{form.lignes.map((l, i) => (
                <TableRow key={i}>
                  <TableCell><Input type="date" value={l.date} onChange={e => updateLine(i, 'date', e.target.value)} /></TableCell>
                  <TableCell><Select value={l.categorie} onValueChange={v => updateLine(i, 'categorie', v)}><SelectTrigger className="text-xs"><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent></Select></TableCell>
                  <TableCell><Input value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" /></TableCell>
                  <TableCell><Switch checked={l.is_kilometrique} onCheckedChange={v => updateLine(i, 'is_kilometrique', v)} /></TableCell>
                  <TableCell>
                    {l.is_kilometrique ? (
                      <div className="flex gap-1"><Input type="number" placeholder="km" className="w-16" value={l.km_distance || ''} onChange={e => updateLine(i, 'km_distance', parseFloat(e.target.value) || 0)} /><Input type="number" placeholder="CV" className="w-14" value={l.km_puissance_fiscale || ''} onChange={e => updateLine(i, 'km_puissance_fiscale', parseInt(e.target.value) || 5)} /></div>
                    ) : (
                      <Input type="number" step="0.01" value={l.montant_ttc} onChange={e => updateLine(i, 'montant_ttc', parseFloat(e.target.value) || 0)} />
                    )}
                  </TableCell>
                  <TableCell>{form.lignes.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}</TableCell>
                </TableRow>
              ))}</TableBody></Table>
              <div className="text-right mt-2"><span className="text-lg font-bold text-[#0F5156]">Total : {formatCurrency(total)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
